import type { UsuarioComCapabilities, UsuarioKeycloak } from '@/data/types';

/**
 * Regras puras da administração de capabilities (sem React, testáveis).
 */

export interface CapabilityGerenciavel {
  valor: string;
  rotulo: string;
  descricao: string;
  /** Só quem já é sysadmin concede/revoga (a API responde 403 para os demais). */
  exigeSysadmin?: boolean;
}

export const CAPABILITIES_GERENCIAVEIS: CapabilityGerenciavel[] = [
  {
    valor: 'admin',
    rotulo: 'Administrador',
    descricao: 'Acesso a tudo: gerencia capabilities e visibilidade, homologa e publica no painel do cliente.',
  },
  {
    valor: 'maintainer',
    rotulo: 'Mantenedor',
    descricao: 'Homologa e reprova versões e gerencia os incidentes.',
  },
  {
    valor: 'painel',
    rotulo: 'Painel do cliente',
    descricao: 'Publica versões no painel do cliente.',
  },
  {
    valor: 'sysadmin',
    rotulo: 'Sysadmin',
    descricao: 'Super role: passa em todas as capabilities. Só quem já é sysadmin pode conceder ou revogar.',
    exigeSysadmin: true,
  },
];

export function rotuloDaCapability(capability: string): string {
  return CAPABILITIES_GERENCIAVEIS.find((item) => item.valor === capability)?.rotulo ?? capability;
}

export function nomeExibido(usuario: Pick<UsuarioKeycloak, 'nome' | 'username' | 'email'>): string {
  return usuario.nome || usuario.username || usuario.email;
}

/**
 * Soma/remove uma capability de UM usuário dentro da lista já carregada, sem
 * recarregar do servidor (é o que torna o toggle otimista): o usuário entra na
 * lista ao ganhar a primeira capability e some ao perder a última. Não muta a
 * lista recebida.
 */
export function atualizarCapabilityNaLista(
  lista: UsuarioComCapabilities[],
  usuario: UsuarioKeycloak,
  capability: string,
  incluir: boolean,
): UsuarioComCapabilities[] {
  const existente = lista.find((item) => item.keycloak_user_id === usuario.keycloak_user_id);

  if (!existente) {
    if (!incluir) return lista;
    return [...lista, { ...usuario, capabilities: [capability] }].sort((a, b) =>
      nomeExibido(a).localeCompare(nomeExibido(b), 'pt-BR'),
    );
  }

  const capabilities = incluir
    ? [...new Set([...existente.capabilities, capability])]
    : existente.capabilities.filter((item) => item !== capability);

  if (capabilities.length === 0) return lista.filter((item) => item.keycloak_user_id !== usuario.keycloak_user_id);
  return lista.map((item) => (item.keycloak_user_id === usuario.keycloak_user_id ? { ...item, capabilities } : item));
}

/** Mesma operação sobre a lista de capabilities de um único usuário. */
export function alternarCapability(capabilities: string[], capability: string, incluir: boolean): string[] {
  return incluir ? [...new Set([...capabilities, capability])] : capabilities.filter((item) => item !== capability);
}
